<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { subDays } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import ReportsAPI from 'dashboard/api/reports';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import ReportHeader from './ReportHeader.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const dateMode = ref('now');
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const inboxId = ref(null);
const isLoading = ref(false);
const hasError = ref(false);
const report = ref({ agents: [], labels: [] });

const since = computed(() =>
  dateMode.value === 'range'
    ? getUnixStartOfDay(customDateRange.value[0])
    : undefined
);
const until = computed(() =>
  dateMode.value === 'range'
    ? getUnixEndOfDay(customDateRange.value[1])
    : undefined
);

const num = value => Number(value || 0);

const agents = computed(() => report.value.agents || []);
const labels = computed(() => report.value.labels || []);

const totalsRow = computed(() => {
  const totals = { total: 0, unlabeled: 0, labels: {} };
  agents.value.forEach(agent => {
    totals.total += num(agent.total);
    totals.unlabeled += num(agent.unlabeled);
    labels.value.forEach(label => {
      totals.labels[label] =
        num(totals.labels[label]) + num(agent.labels?.[label]);
    });
  });
  return totals;
});

let requestId = 0;
const fetchReport = async () => {
  requestId += 1;
  const currentRequest = requestId;
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await ReportsAPI.getAgentLabelMatrix({
      since: since.value,
      until: until.value,
      inboxId: inboxId.value || undefined,
    });
    if (currentRequest !== requestId) return;
    report.value = {
      agents: data?.agents || [],
      labels: data?.labels || [],
    };
  } catch (error) {
    if (currentRequest !== requestId) return;
    hasError.value = true;
    if (currentRequest !== requestId) return;
    report.value = { agents: [], labels: [] };
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
};

const setDateMode = mode => {
  if (dateMode.value === mode) return;
  dateMode.value = mode;
  fetchReport();
};

const onDateRangeChange = ([startDate, endDate, rangeType]) => {
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchReport();
};

const onInboxChange = () => {
  fetchReport();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchReport();
});
</script>

<template>
  <ReportHeader :header-title="$t('REPORT.AGENT_LABELS.TITLE')" />
  <p class="px-4 text-xs text-n-slate-11">
    {{ $t('REPORT.CAMPAIGN_PERFORMANCE.AGENT_SEMANTICS') }}
  </p>
  <div class="flex flex-col flex-1 gap-6">
    <div
      class="flex flex-col flex-wrap w-full gap-3 md:flex-row md:items-center"
    >
      <div
        class="inline-flex rounded-lg border border-n-weak bg-n-alpha-black2"
      >
        <button
          type="button"
          class="px-3 py-2 text-sm rounded-l-lg"
          :class="
            dateMode === 'now'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="setDateMode('now')"
        >
          {{ $t('REPORT.AGENT_LABELS.DATE_MODE_NOW') }}
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm rounded-r-lg"
          :class="
            dateMode === 'range'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="setDateMode('range')"
        >
          {{ $t('REPORT.AGENT_LABELS.DATE_MODE_RANGE') }}
        </button>
      </div>

      <WootDatePicker
        v-if="dateMode === 'range'"
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />

      <select
        v-model="inboxId"
        class="w-full md:w-60 mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
        @change="onInboxChange"
      >
        <option :value="null">
          {{ $t('REPORT.AGENT_LABELS.ALL_INBOXES') }}
        </option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
    </div>

    <div
      v-if="hasError"
      class="p-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
    >
      {{ $t('REPORT.AGENT_LABELS.ERROR') }}
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <template v-else>
      <div class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full text-sm text-left">
          <thead class="text-n-slate-11 bg-n-alpha-black2">
            <tr>
              <th class="px-4 py-3 font-medium whitespace-nowrap">
                {{ $t('REPORT.AGENT_LABELS.TABLE.AGENT') }}
              </th>
              <th class="px-4 py-3 font-medium text-right whitespace-nowrap">
                {{ $t('REPORT.AGENT_LABELS.TABLE.TOTAL') }}
              </th>
              <th
                v-for="label in labels"
                :key="label"
                class="px-4 py-3 font-medium text-right whitespace-nowrap"
              >
                {{ label }}
              </th>
              <th class="px-4 py-3 font-medium text-right whitespace-nowrap">
                {{ $t('REPORT.AGENT_LABELS.TABLE.UNLABELED') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="!agents.length">
              <td
                :colspan="labels.length + 3"
                class="px-4 py-6 text-center text-n-slate-11"
              >
                {{ $t('REPORT.AGENT_LABELS.TABLE.NO_DATA') }}
              </td>
            </tr>
            <tr
              v-for="agent in agents"
              :key="agent.id ?? 'unassigned'"
              class="text-n-slate-12"
              :class="{ 'italic text-n-slate-11': agent.id === null }"
            >
              <td class="px-4 py-3 whitespace-nowrap">
                {{ agent.name }}
              </td>
              <td class="px-4 py-3 text-right tabular-nums">
                {{ num(agent.total) }}
              </td>
              <td
                v-for="label in labels"
                :key="label"
                class="px-4 py-3 text-right tabular-nums"
              >
                {{ num(agent.labels && agent.labels[label]) }}
              </td>
              <td class="px-4 py-3 text-right tabular-nums">
                {{ num(agent.unlabeled) }}
              </td>
            </tr>
          </tbody>
          <tfoot
            v-if="agents.length"
            class="font-medium border-t-2 border-n-weak text-n-slate-12 bg-n-alpha-black2"
          >
            <tr>
              <td class="px-4 py-3 whitespace-nowrap">
                {{ $t('REPORT.AGENT_LABELS.TABLE.TOTAL') }}
              </td>
              <td class="px-4 py-3 text-right tabular-nums">
                {{ num(totalsRow.total) }}
              </td>
              <td
                v-for="label in labels"
                :key="label"
                class="px-4 py-3 text-right tabular-nums"
              >
                {{ num(totalsRow.labels[label]) }}
              </td>
              <td class="px-4 py-3 text-right tabular-nums">
                {{ num(totalsRow.unlabeled) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </div>
</template>
